interface NiiVueColormap {
  R: number[]
  G: number[]
  B: number[]
  A: number[]
  I: number[]
  labels: string[]
}

export function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "")
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  return [r, g, b]
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l * 100]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  switch (max) {
    case rn:
      h = (gn - bn) / d + (gn < bn ? 6 : 0)
      break
    case gn:
      h = (bn - rn) / d + 2
      break
    default:
      h = (rn - gn) / d + 4
      break
  }
  return [h * 60, s * 100, l * 100]
}

function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100
  const ln = l / 100
  const c = (1 - Math.abs(2 * ln - 1)) * sn
  const hp = (((h % 360) + 360) % 360) / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r1 = 0
  let g1 = 0
  let b1 = 0
  if (hp < 1) [r1, g1, b1] = [c, x, 0]
  else if (hp < 2) [r1, g1, b1] = [x, c, 0]
  else if (hp < 3) [r1, g1, b1] = [0, c, x]
  else if (hp < 4) [r1, g1, b1] = [0, x, c]
  else if (hp < 5) [r1, g1, b1] = [x, 0, c]
  else [r1, g1, b1] = [c, 0, x]
  const m = ln - c / 2
  const to2 = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0")
  return `#${to2(r1)}${to2(g1)}${to2(b1)}`
}

const GOLDEN_ANGLE_DEG = 137.508
const HUES_PER_CYCLE = 8
const LIGHTNESS_MIN = 25
const LIGHTNESS_MAX = 75

function cycleLightnessShift(cycle: number): number {
  if (cycle <= 0) return 0
  const magnitude = Math.ceil(cycle / 2) * 12
  return cycle % 2 === 1 ? magnitude : -magnitude
}

// Derive a distinct color for a specific instance of a multi-instance mask label.
// Pure: same (baseHex, ordinal) always returns the same hex. Instance 1 returns
// the base color unchanged, so single-instance labels render identically.
export function deriveInstanceColor(baseHex: string, ordinal: number): string {
  if (ordinal <= 1) return baseHex
  const [r, g, b] = hexToRgb(baseHex)
  const [h, s, l] = rgbToHsl(r, g, b)
  const index = ordinal - 1
  const cycle = Math.floor(index / HUES_PER_CYCLE)
  const slot = index % HUES_PER_CYCLE
  const newHue = (h + slot * GOLDEN_ANGLE_DEG) % 360
  const newLightness = Math.max(LIGHTNESS_MIN, Math.min(LIGHTNESS_MAX, l + cycleLightnessShift(cycle)))
  return hslToHex(newHue, s, newLightness)
}

// Creates a 256-entry colormap for a single label:
//   Index 0: transparent (background)
//   Index 1–255: the label's color, fully opaque
export function createSingleLabelColormap(color: string): NiiVueColormap {
  const [r, g, b] = hexToRgb(color)
  const R = new Array<number>(256).fill(r)
  const G = new Array<number>(256).fill(g)
  const B = new Array<number>(256).fill(b)
  const A = new Array<number>(256).fill(255)
  const I = Array.from({ length: 256 }, (_, i) => i)
  const labels: string[] = []

  // Index 0 is transparent background
  R[0] = 0
  G[0] = 0
  B[0] = 0
  A[0] = 0

  return { R, G, B, A, I, labels }
}
